class CollectionDescriptionToggle extends HTMLElement {
  connectedCallback() {
    if (this.classList.contains("disable-readmore")) return;

    this.shortBlock = this.querySelector(".short");
    this.fullBlock = this.querySelector(".full");
    this.toggleButton = this.querySelector(".read-more-button");
    this.readlessButton = this.querySelector('.read-less-button');

    // Truncate text to fit 2 lines
    this.truncateToTwoLines();

    this.toggleButton?.addEventListener("click", this.clickedReadMore.bind(this));
    this.readlessButton?.addEventListener("click", this.clickedReadLess.bind(this));

    // Re-truncate on screen size change
    this._resizeObserver = new ResizeObserver(() => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        // Only re-truncate if short block is visible (not in "read more" state)
        if (!this.shortBlock.classList.contains('hide')) {
          this.truncateToTwoLines();
        }
      }, 200);
    });

    this._resizeObserver.observe(document.body);
  }

  disconnectedCallback() {
    // Clean up observer when element is removed
    this._resizeObserver?.disconnect();
  }

  truncateToTwoLines() {
    if (!this.fullBlock || !this.shortBlock || !this.toggleButton) return;

    // Strip existing buttons text if it somehow got into the description
    let fullText = this.fullBlock.innerText.trim().replace(/Read more|Read less/g, '').replace(/\.\.\.\s*$/, '').trim();
    const shortBlock = this.shortBlock;
    const button = this.toggleButton;
    const lessButton = this.readlessButton;

    // Temporarily show to measure
    const originalDisplay = shortBlock.style.display;
    shortBlock.style.visibility = 'hidden';
    shortBlock.style.display = 'block';
    shortBlock.style.height = 'auto';

    // Get line height and max height for 2 lines
    const style = window.getComputedStyle(shortBlock);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
    const maxHeight = Math.ceil(lineHeight * 2) + 2; // Small buffer for sub-pixel rendering

    // Check if full text already fits
    shortBlock.innerHTML = fullText;
    if (shortBlock.scrollHeight <= maxHeight) {
      button.style.display = 'none';
      if (lessButton) lessButton.style.display = 'none';
      shortBlock.style.visibility = '';
      shortBlock.style.display = originalDisplay;
      return;
    }

    // Truncate if it doesn't fit
    button.style.display = 'inline';
    let lo = 0, hi = fullText.length;
    while (lo < hi) {
      const mid = Math.floor((lo + hi + 1) / 2);
      shortBlock.innerHTML = fullText.slice(0, mid) + '... ';
      shortBlock.appendChild(button);
      if (shortBlock.scrollHeight <= maxHeight) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }

    // Set final truncated text
    shortBlock.innerHTML = fullText.slice(0, lo) + '... ';
    shortBlock.appendChild(button);

    // Reset visibility
    shortBlock.style.visibility = '';
    shortBlock.style.display = originalDisplay;
  }

  clickedReadMore() {
    this.shortBlock.classList.add('hide');
    this.fullBlock.classList.remove('hide');
    if (this.readlessButton) this.readlessButton.style.display = 'inline';
  }

  clickedReadLess() {
    this.shortBlock.classList.remove('hide');
    this.fullBlock.classList.add('hide');
    this.truncateToTwoLines();
  }
}

customElements.define("collection-description-toggle", CollectionDescriptionToggle);
class CollectionLinksScrollbar extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.releated-collection-links');
    this.thumb = this.querySelector('.custom-scrollbar-thumb');
    this.track = this.querySelector('.custom-scrollbar-track');

    if (!this.list || !this.thumb || !this.track) return;

    this.list.addEventListener('scroll', this.updateThumb.bind(this));
    
    // Dragging logic for the list
    this.isDraggingList = false;
    this.startX = 0;
    this.scrollLeftStart = 0;

    this.list.addEventListener('mousedown', this.startDraggingList.bind(this));
    window.addEventListener('mousemove', this.dragList.bind(this));
    window.addEventListener('mouseup', this.stopDraggingList.bind(this));

    // Dragging logic for the thumb
    this.isDraggingThumb = false;
    this.startThumbX = 0;
    this.startScrollLeftThumb = 0;

    this.thumb.addEventListener('mousedown', this.startDraggingThumb.bind(this));
    window.addEventListener('mousemove', this.dragThumb.bind(this));
    window.addEventListener('mouseup', this.stopDraggingThumb.bind(this));

    // Initial update after fonts are ready
    if (document.fonts) {
      document.fonts.ready.then(() => {
        this.updateThumb();
      });
    } else {
      setTimeout(() => this.updateThumb(), 200);
    }

    // Monitor size and content changes
    this._resizeObserver = new ResizeObserver(() => {
      this.updateThumb();
    });
    this._resizeObserver.observe(this.list);
    if (this.list.firstElementChild) {
      this._resizeObserver.observe(this.list.firstElementChild);
    }
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    window.removeEventListener('mousemove', this.dragList.bind(this));
    window.removeEventListener('mouseup', this.stopDraggingList.bind(this));
    window.removeEventListener('mousemove', this.dragThumb.bind(this));
    window.removeEventListener('mouseup', this.stopDraggingThumb.bind(this));
  }

  startDraggingList(e) {
    // Only drag with left mouse button
    if (e.button !== 0) return;
    
    this.isDraggingList = true;
    this.list.style.scrollBehavior = 'auto';
    this.list.style.scrollSnapType = 'none';
    this.list.style.cursor = 'grabbing';
    
    this.startX = e.pageX - this.list.offsetLeft;
    this.scrollLeftStart = this.list.scrollLeft;
  }

  dragList(e) {
    if (!this.isDraggingList) return;
    e.preventDefault();
    
    const x = e.pageX - this.list.offsetLeft;
    const walk = (x - this.startX) * 1.5; // Scroll speed multiplier
    this.list.scrollLeft = this.scrollLeftStart - walk;
  }

  stopDraggingList() {
    if (!this.isDraggingList) return;
    this.isDraggingList = false;
    this.list.style.scrollBehavior = '';
    this.list.style.scrollSnapType = '';
    this.list.style.cursor = '';
  }

  startDraggingThumb(e) {
    if (e.button !== 0) return;
    
    this.isDraggingThumb = true;
    this.list.style.scrollBehavior = 'auto';
    this.list.style.scrollSnapType = 'none';
    
    this.startThumbX = e.pageX;
    this.startScrollLeftThumb = this.list.scrollLeft;
    
    e.preventDefault();
    e.stopPropagation();
  }

  dragThumb(e) {
    if (!this.isDraggingThumb) return;
    e.preventDefault();

    const dx = e.pageX - this.startThumbX;
    const trackWidth = this.track.clientWidth;
    const thumbWidth = this.thumb.clientWidth;
    const maxThumbMove = trackWidth - thumbWidth;
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;

    if (maxThumbMove <= 0) return;

    const scrollChange = (dx / maxThumbMove) * maxScroll;
    this.list.scrollLeft = this.startScrollLeftThumb + scrollChange;
  }

  stopDraggingThumb() {
    if (!this.isDraggingThumb) return;
    this.isDraggingThumb = false;
    this.list.style.scrollBehavior = '';
    this.list.style.scrollSnapType = '';
  }

  updateThumb() {
    const scrollLeft = this.list.scrollLeft;
    const scrollWidth = this.list.scrollWidth;
    const clientWidth = this.list.clientWidth;
    
    const container = this.querySelector('.custom-scrollbar-container');
    if (!container) return;

    if (scrollWidth > clientWidth + 1) {
      container.style.display = 'flex';
    } else {
      container.style.display = 'none';
      return;
    }

    const maxScroll = scrollWidth - clientWidth;
    const scrollProgress = Math.max(0, Math.min(1, scrollLeft / maxScroll));
    
    const trackWidth = this.track.clientWidth;
    const thumbWidth = this.thumb.clientWidth;
    const maxThumbMove = trackWidth - thumbWidth;
    
    const thumbMove = scrollProgress * maxThumbMove;
    this.thumb.style.transform = `translateX(${thumbMove}px)`;
  }
}

customElements.define('collection-links-scrollbar', CollectionLinksScrollbar);
